import { App, Button, Popconfirm, Space } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  usePatientArchive,
  usePatientRemove,
  usePatientUnarchive,
} from '../../libs/ohat-api';
import { getApiErrorMessage } from '../../utils/apiError';

type Props = {
  patientId: string;
  archived: boolean;
  /** アーカイブ成功後の処理（未指定ならホームへ遷移） */
  onArchived?: () => void;
  /** アーカイブ解除成功後の処理 */
  onUnarchived?: () => void;
};

/**
 * 患者のアーカイブ / アーカイブ解除ボタン。
 * 無料プランを含む全プランで利用できる。
 */
export const PatientArchiveButton = ({
  patientId,
  archived,
  onArchived,
  onUnarchived,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { mutateAsync: archivePatient } = usePatientArchive();
  const { mutateAsync: unarchivePatient } = usePatientUnarchive();
  const { mutateAsync: removePatient } = usePatientRemove();

  const handleArchive = async () => {
    try {
      await archivePatient({ id: patientId });
      message.success(t('patient.archived'));
      if (onArchived) onArchived();
      else navigate('/'); // アーカイブ後は一覧から非表示になるためホームへ
    } catch (e) {
      message.error(getApiErrorMessage(e, t('patient.archiveFailed')));
    }
  };

  const handleUnarchive = async () => {
    try {
      await unarchivePatient({ id: patientId });
      message.success(t('patient.unarchived'));
      onUnarchived?.();
    } catch (e) {
      message.error(getApiErrorMessage(e, t('patient.archiveFailed')));
    }
  };

  const handleDelete = async () => {
    try {
      await removePatient({ id: patientId });
      message.success(t('patient.deleted'));
      navigate('/'); // 物理削除後は一覧へ
    } catch (e) {
      message.error(getApiErrorMessage(e, t('patient.deleteFailed')));
    }
  };

  if (archived) {
    // アーカイブ済みは「解除」に加えて完全物理削除も可能にする。
    return (
      <Space>
        <Button type="link" icon={<InboxOutlined />} onClick={handleUnarchive}>
          {t('patient.unarchive')}
        </Button>
        <Popconfirm
          title={t('patient.deleteConfirm')}
          onConfirm={handleDelete}
          okText={t('patient.delete')}
          okButtonProps={{ danger: true }}
          cancelText={t('common.cancel')}
        >
          <Button type="link" danger icon={<DeleteOutlined />}>
            {t('patient.delete')}
          </Button>
        </Popconfirm>
      </Space>
    );
  }

  return (
    <Popconfirm
      title={t('patient.archiveConfirm')}
      onConfirm={handleArchive}
      okText={t('patient.archive')}
      cancelText={t('common.cancel')}
    >
      <Button type="link" danger icon={<InboxOutlined />}>
        {t('patient.archive')}
      </Button>
    </Popconfirm>
  );
};
